import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ModelObject } from '@cco/model';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ResponseDtoService } from './response-dto.service';

export const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

// @Injectable()
export abstract class StandardService<T extends ModelObject<I>, I> extends ResponseDtoService {
  static httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  protected constructor(
    protected http: HttpClient,
    protected baseUrl: string,
    protected relativeUrl: string,
  ) {
    super(baseUrl);
  }

  add(obj: T): Observable<T> {
    const mappedObj = this.reverseMapModelObject(obj);
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http.post<T>(baseUrl + this.relativeUrl, mappedObj, httpOptions).pipe(
        map(response => this.mapModelObject.bind(this)(response)))));
  }

  get(id: I): Observable<T> {
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http.get<T>(`${baseUrl + this.relativeUrl}/${id}`).pipe(
        map(response => this.mapModelObject.bind(this)(response)))));
  }

  getAll(): Observable<T[]> {
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http
      .get<T[]>(baseUrl + this.relativeUrl)
      .pipe(map(response => this.mapModelObjects.bind(this)(response)))));
  }

  update(obj: T): Observable<T> {
    const mappedObj = this.reverseMapModelObject(obj);
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http
      .put<T>(baseUrl + this.relativeUrl, mappedObj, httpOptions)
      .pipe(map(response => this.mapModelObject.bind(this)(response)))));
  }

  delete(id: I) {
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http.delete(`${baseUrl + this.relativeUrl}/${id}`)));
  }

  public mapModelObject(modelObject: T): T {
    return this.mapObject(modelObject);
  }

  public mapModelObjects(modelObjects: T[]): T[] {
    const newObjects: T[] = [];

    modelObjects.forEach(obj => newObjects.push(this.mapModelObject(obj)));

    return newObjects;
  }

  public reverseMapModelObject(modelObject: T): { [__source_class: string]: T } | T {
    if (!modelObject || !modelObject.__source_class) {
      return modelObject;
    }

    const mappedObject = {};

    mappedObject[modelObject.__source_class] = modelObject;

    return mappedObject;
  }

  public reverseMapModelObjects(modelObjects: T[]): { [__source_class: string]: T }[] | T[] {
    const newObjects = [];

    modelObjects.forEach(modelObject => newObjects.push(this.reverseMapModelObject(modelObject)));

    return modelObjects;
  }

  public mapObject<S>(object: S): S {
    return object;
  }

  public mapObjectArray<S>(objects: S[]): S[] {
    const newObjects: S[] = [];

    objects.forEach(obj => newObjects.push(this.mapObject(obj)));

    return newObjects;
  }
}
